/** Write a German paragraph and have it marked.
 *
 * The client's ask, in their words: "where the student can write some German
 * paragraph and then it can be scored by AI agent and at the end then they
 * get recommendations, as we get in speak with AI agent."
 *
 * The coach behind this already graded exam tasks; the only thing missing
 * was a door into it that isn't an exam. The learner picks the level to be
 * judged against, writes, and gets back a score, corrections and what to
 * practise next — the same feedback the exam task gives.
 */
import { useEffect, useState } from "react";
import * as api from "../../api/writing";
import { ApiError } from "../../api/http";
import { Badge, Button, Card, EmptyState, Spinner } from "../../components/ui";
import { useT } from "../../i18n";

const BOX =
  "w-full rounded-control border border-line bg-surface px-3 py-2 text-sm text-ink outline-none transition-colors placeholder:text-muted focus:border-brand";

/** Below this the coach refuses anyway — say so before they press. */
const MIN_CHARS = 20;

export function WritingPractice() {
  const t = useT();
  const [state, setState] = useState<api.FreeWritingState | null>(null);
  const [text, setText] = useState("");
  const [level, setLevel] = useState("B1");
  const [topic, setTopic] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<api.FreeWriting | null>(null);

  useEffect(() => {
    let cancelled = false;
    api
      .freeWritingState()
      .then((s) => {
        if (cancelled) return;
        setState(s);
        if (s.levels.includes("B1")) setLevel("B1");
      })
      .catch(() => {
        if (!cancelled) {
          setState({ configured: false, quota: null, levels: [], submissions: [] });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  if (state === null) {
    return (
      <div className="grid place-items-center py-8">
        <Spinner size={24} />
      </div>
    );
  }

  if (!state.configured) {
    return (
      <Card>
        <p role="status" className="text-sm text-muted">{t("writing.notConfigured")}</p>
      </Card>
    );
  }

  const tooShort = text.trim().length < MIN_CHARS;

  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      const marked = await api.submitFreeWriting(text.trim(), level, topic.trim());
      setResult(marked);
      setText("");
      setTopic("");
      setState((s) =>
        s ? { ...s, quota: marked.quota, submissions: [marked, ...s.submissions] } : s,
      );
    } catch (e) {
      setError(e instanceof ApiError ? e.message : t("common.error.generic"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="space-y-4">
      {result ? (
        <Card>
          <Marked piece={result} onAgain={() => setResult(null)} />
        </Card>
      ) : (
        <Card>
          <p className="text-sm text-ink">{t("writing.intro")}</p>

          <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label htmlFor="writing-level" className="text-sm font-semibold text-ink">
                {t("writing.level")}
              </label>
              <select
                id="writing-level"
                value={level}
                onChange={(e) => setLevel(e.target.value)}
                className="mt-1 w-full rounded-control border border-line bg-surface px-3 py-2 text-sm text-ink"
              >
                {state.levels.map((l) => (
                  <option key={l} value={l}>{l}</option>
                ))}
              </select>
              <p className="mt-1 text-xs text-muted">{t("writing.levelHint")}</p>
            </div>
            <div>
              <label htmlFor="writing-topic" className="text-sm font-semibold text-ink">
                {t("writing.topic")}
              </label>{" "}
              <span className="text-xs text-muted">{t("writing.topicHint")}</span>
              <input
                id="writing-topic"
                value={topic}
                onChange={(e) => setTopic(e.target.value)}
                placeholder={t("writing.topicPlaceholder")}
                className={`mt-1 ${BOX}`}
              />
            </div>
          </div>

          <div className="mt-4">
            <label htmlFor="writing-text" className="text-sm font-semibold text-ink">
              {t("writing.yourText")}
            </label>
            <textarea
              id="writing-text"
              rows={9}
              value={text}
              onChange={(e) => setText(e.target.value)}
              placeholder={t("writing.placeholder")}
              className={`mt-1 ${BOX}`}
            />
            <p className="mt-1 text-xs text-muted">
              {`${text.trim() ? text.trim().split(/\s+/).length : 0} ${t("writing.words")}`}
            </p>
          </div>

          {error && <p role="alert" className="mt-3 text-sm text-danger">{error}</p>}

          <div className="mt-4 flex items-center justify-between">
            <span className="text-xs text-muted">
              {state.quota &&
                t("speaking.quotaUsed", {
                  used: state.quota.used, limit: state.quota.limit,
                })}
            </span>
            <Button onClick={() => void submit()} loading={busy} disabled={tooShort}>
              {t("writing.submit")}
            </Button>
          </div>
        </Card>
      )}

      {state.submissions.length > 0 && (
        <Card title={t("writing.past")}>
          <ul className="divide-y divide-line">
            {state.submissions.map((row) => (
              <li key={row.id}>
                <button
                  onClick={() => setResult(row)}
                  className="flex w-full items-center justify-between gap-3 py-3 text-left hover:text-brand"
                >
                  <span className="text-sm text-ink">{label(row)}</span>
                  <span className="shrink-0 text-xs text-muted">
                    {row.score_percent !== null ? `${row.score_percent}%` : "—"}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        </Card>
      )}
    </div>
  );
}

/** "12 Mar 2026 · Mein Wochenende · B1 · 84 words" — whatever it has. */
function label(row: api.FreeWriting): string {
  const when = row.created_at
    ? new Date(row.created_at).toLocaleDateString(undefined, {
        day: "numeric", month: "short", year: "numeric",
      })
    : "";
  return [when, row.topic, row.level].filter(Boolean).join(" · ");
}

function Marked({ piece, onAgain }: { piece: api.FreeWriting; onAgain: () => void }) {
  const t = useT();
  const fb = piece.feedback;
  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm text-muted">{label(piece)}</p>
          {fb && <Badge tone="brand">{fb.level_estimate}</Badge>}
        </div>
        <p className="text-3xl font-extrabold text-ink">
          {piece.score_percent !== null ? `${piece.score_percent}%` : "—"}
        </p>
      </div>

      {fb ? (
        <>
          <p className="text-sm text-ink">{fb.overall_comment}</p>

          {fb.corrections.length > 0 && (
            <div>
              <h3 className="font-bold text-ink">{t("writing.corrections")}</h3>
              <ul className="mt-2 space-y-2">
                {fb.corrections.map((c, i) => (
                  <li key={i} className="rounded-control bg-canvas p-3 text-sm">
                    <p className="text-danger line-through">{c.original}</p>
                    <p className="font-semibold text-ink">{c.corrected}</p>
                    <p className="mt-1 text-xs text-muted">{c.explanation}</p>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {fb.improvements.length > 0 && (
            <div>
              <h3 className="font-bold text-ink">{t("writing.improvements")}</h3>
              <ul className="mt-2 list-disc space-y-1 pl-5 text-sm text-ink">
                {fb.improvements.map((s, i) => <li key={i}>{s}</li>)}
              </ul>
            </div>
          )}
        </>
      ) : (
        <EmptyState message={t("writing.noFeedback")} />
      )}

      <div>
        <h3 className="font-bold text-ink">{t("writing.whatYouWrote")}</h3>
        <p className="mt-2 whitespace-pre-wrap rounded-control bg-canvas p-3 text-sm text-ink">
          {piece.text}
        </p>
      </div>

      <Button variant="secondary" onClick={onAgain}>{t("writing.again")}</Button>
    </div>
  );
}
